/* ============================================================
   SITE CONTENT — Phase 1: content lives in code.
   This is the seed for the Firestore content model in Phase 2
   (shape mirrors plan.md §6 / the prototype's defaults).
   ============================================================ */

// Gradient "photo" placeholder generator (warm-dark, on brand).
// Real client photography replaces these via Storage in Phase 4.
export function gradImg(h1, h2, ang = 145) {
  return `linear-gradient(${ang}deg,
     oklch(0.26 0.03 ${h1}) 0%,
     oklch(0.17 0.02 ${h1}) 42%,
     oklch(0.13 0.015 ${h2}) 100%)`
}

export const MEDIA = [
  { id: 'm_lobby', name: 'LOBBY_NIGHT', css: gradImg(95, 60, 150) },
  { id: 'm_table', name: 'THE_TABLE', css: gradImg(60, 40, 135) },
  { id: 'm_atelier', name: 'ATELIER_LOW', css: gradImg(140, 110, 160) },
  { id: 'm_wardrobe', name: 'WARDROBE_WARM', css: gradImg(45, 30, 120) },
  { id: 'm_retreat', name: 'RETREAT_DUSK', css: gradImg(160, 120, 150) },
  { id: 'm_sanct', name: 'SANCTUARY_STONE', css: gradImg(110, 80, 140) },
  { id: 'm_philo', name: 'QUIET_ROOM', css: gradImg(130, 90, 155) },
  { id: 'm_estate', name: 'ESTATE_DUSK', css: gradImg(80, 50, 140) },
  { id: 'm_og', name: 'OG_MASTER', css: gradImg(120, 70, 150) },
]

const SCENES = [
  { id: 's1', label: 'THE LOBBY', media: 'm_lobby' },
  { id: 's2', label: 'THE TABLE', media: 'm_table' },
  { id: 's3', label: 'THE ATELIER', media: 'm_atelier' },
  { id: 's4', label: 'THE WARDROBE', media: 'm_wardrobe' },
  { id: 's5', label: 'THE RETREAT', media: 'm_retreat' },
  { id: 's6', label: 'THE SANCTUARY', media: 'm_sanct' },
]

const DISCIPLINES = [
  {
    idx: '01',
    title: 'POSITION',
    icon: 'crosshair',
    intro: 'Position is constructed, then held.',
    keywords: 'Brand defines it. Channels shape it. Press introduces it. Voice sustains it.',
    category: 'IDENTITY & PLACE',
    items: [
      {
        code: 'P.1',
        title: 'BRAND',
        tag: 'DEFINITION',
        note: 'The house is named and expressed in full.',
      },
      { code: 'P.2', title: 'CHANNEL', tag: 'STRUCTURE', note: 'Visibility is built with intent.' },
      {
        code: 'P.3',
        title: 'PRESS',
        tag: 'INTRODUCTION',
        note: 'The house is placed, not pushed.',
      },
      { code: 'P.4', title: 'VOICE', tag: 'CONTINUITY', note: 'Every expression stays in tune.' },
    ],
    closing: 'Position, once set, is not chased. It is kept.',
  },
  {
    idx: '02',
    title: 'EXPERIENCE',
    icon: 'ring',
    intro: 'Experience is composed, not improvised.',
    keywords: 'Arrival sets the tone. Atmosphere holds it. Service carries it. Memory keeps it.',
    category: 'SENSE & RHYTHM',
    items: [
      { code: 'E.1', title: 'ARRIVAL', tag: 'THRESHOLD', note: 'The first minute is designed.' },
      {
        code: 'E.2',
        title: 'ATMOSPHERE',
        tag: 'COMPOSITION',
        note: 'Light, scent, and quiet are arranged.',
      },
      { code: 'E.3', title: 'SERVICE', tag: 'CHOREOGRAPHY', note: 'Care moves unseen.' },
      { code: 'E.4', title: 'MEMORY', tag: 'ECHO', note: 'What lingers is what was meant.' },
    ],
    closing: "Experience isn't performed. It's felt, then remembered.",
  },
  {
    idx: '03',
    title: 'GROWTH',
    icon: 'arc',
    intro: 'Growth is cultivated, never forced.',
    keywords: 'Demand is grown. Presence is structured. Partners are chosen. Worth is held.',
    category: 'DEMAND & DURABILITY',
    items: [
      { code: 'G.1', title: 'MARKET', tag: 'ENTRY', note: 'A new place is entered with intent.' },
      {
        code: 'G.2',
        title: 'REVENUE',
        tag: 'ARCHITECTURE',
        note: 'Yield is designed, not hoped for.',
      },
      { code: 'G.3', title: 'DEMAND', tag: 'CONVERSION', note: 'Interest becomes return.' },
      {
        code: 'G.4',
        title: 'PARTNERS',
        tag: 'ALIGNMENT',
        note: 'Capital and house move together.',
      },
    ],
    closing: "Growth isn't rushed. It's held and sustained.",
  },
  {
    idx: '04',
    title: 'PERFORMANCE',
    icon: 'nested',
    intro: 'Performance is held, not spiked.',
    keywords:
      'Yield is structured. Margin is aligned. Value is sustained. Each choice makes room for the next.',
    category: 'STEWARDSHIP OF VALUE',
    items: [
      {
        code: 'F.1',
        title: 'ASSET',
        tag: 'POSITIONING',
        note: 'The worth of the house is defined.',
      },
      { code: 'F.2', title: 'FINANCE', tag: 'STRATEGY', note: 'Profit is designed.' },
      { code: 'F.3', title: 'OPERATIONS', tag: 'DISCIPLINE', note: 'The day runs in control.' },
      { code: 'F.4', title: 'PORTFOLIO', tag: 'ADVISORY', note: 'Value is held across holdings.' },
    ],
    closing: "Performance isn't maximised. It's sustained.",
  },
  {
    idx: '05',
    title: 'ALIGNMENT',
    icon: 'node',
    intro: 'We set capital and structure in one hand.',
    keywords:
      'Investors we introduce. Capital we align. Operations we structure. Governance we hold.',
    category: 'ALIGNMENT & GOVERNANCE',
    items: [
      {
        code: 'A.1',
        title: 'INVESTOR',
        tag: 'INTRODUCTION',
        note: 'We bring capital forward, and keep it close.',
      },
      {
        code: 'A.2',
        title: 'CAPITAL',
        tag: 'ALIGNMENT',
        note: 'Interests match before terms are written.',
      },
      {
        code: 'A.3',
        title: 'OPERATIONAL',
        tag: 'STRUCTURE',
        note: 'We build the house to hold what it gathers.',
      },
      { code: 'A.4', title: 'GOVERNANCE', tag: 'CONTINUITY', note: 'Decisions outlast the room.' },
    ],
    closing: 'Alignment isn’t negotiated. We compose it, and keep it.',
  },
]

// Active locale. v1 ships EN only; the schema stays locale-aware so
// adding a language later is data entry, not a refactor.
export const DEFAULT_LOCALE = 'EN'

export const CONTENT = {
  brand: 'CuratedHomes',
  footer: {
    tagline: 'Known by those who keep their own counsel.',
    micro: '© 2026 · BY REFERRAL · IN CONFIDENCE',
  },
  hero: {
    label: 'ADVISORY · BY REFERRAL',
    pre: 'We build the quiet ',
    em: 'authority',
    post: ' of exceptional houses.',
    sub: 'Reputation, revenue, and worth — advised in confidence.',
  },
  philosophy: {
    pre: 'A house is not promoted. It is ',
    em: 'placed',
    post: '.',
    quote: 'We don’t follow the field. We sit a step to one side of it, by mandate.',
    body1:
      'We work with a small number of houses at a time, and only those we believe in. The relationship is long, quiet, and exclusive — a mandate, not a campaign.',
    body2:
      'What we do is rarely visible. The result is a house that is known by the right people, in the right rooms, for the right reasons.',
    caption: 'A QUIET AGREEMENT',
    image: 'm_philo',
  },
  method: {
    pre: 'Every engagement opens with a ',
    em: 'reading',
    post: '.',
    overline: 'SCENES, ONE REGISTER',
    statement: 'Reading precedes design. Design precedes growth.',
    subtext: 'THE ORDER IS NEVER REVERSED',
    scenes: SCENES,
  },
  disciplines: {
    pre: 'Five disciplines. One ',
    em: 'standard',
    post: '.',
    blocks: DISCIPLINES,
  },
  engagements: {
    pre: 'What we take on. How we ',
    em: 'move',
    post: '.',
    threeTitle: 'THREE ENGAGEMENTS — What we take on',
    three: [
      {
        n: '01',
        title: 'Opening Houses',
        text: 'Repositioning the asset: when to step forward, and when to hold back.',
      },
      {
        n: '02',
        title: 'Investor Alignment',
        text: 'Capital introduction and long-horizon positioning, kept among the few.',
      },
      {
        n: '03',
        title: 'Curated Concepts',
        text: 'Integrated direction. Worth held to the next decade, not the next quarter.',
      },
    ],
    statement: 'Nothing is added without reason. Nothing grows past what the house can hold.',
    movesTitle: 'THE METHOD, IN THREE MOVES — How we move',
    moves: [
      {
        n: '01',
        title: 'We read',
        text: 'The asset, its ownership, and its place — said and unsaid.',
      },
      {
        n: '02',
        title: 'We draw',
        text: 'Position is shaped from what we find, never forced onto it.',
      },
      { n: '03', title: 'We hold', text: 'Measured by the next decade, not the next quarter.' },
    ],
    housesTitle: 'THE HOUSES WE KEEP',
    houses: [
      'Independent houses of hospitality.',
      'Private estates, residences, and retreats.',
      'Tables and culinary houses of singular character.',
      'Maisons of craft, heritage, and quiet renown.',
    ],
    regionsPre: 'EUROPE · ARABIA · ASIA · THE AMERICAS',
    regionsEm: 'BY PLACE, NEVER BY MAP',
  },
  difference: {
    pre: 'An ',
    em: 'advisory',
    post: ', not an agency.',
    headA: 'THE AGENCY',
    headB: 'THE ADVISORY',
    rows: [
      { a: 'Sells hours', b: 'Holds a mandate' },
      { a: 'Competes for briefs', b: 'Chooses its houses' },
      { a: 'Ships a deck', b: 'Builds a position' },
      { a: 'Scales accounts', b: 'Tends a portfolio' },
      { a: 'Advertises itself', b: 'Arrives by referral' },
    ],
    statement: 'Engagement is offered. Commitment is assumed.',
  },
  enquiry: {
    pre: 'Start with a ',
    em: 'confidential',
    post: ' brief.',
    lead: 'A short, private note is enough to begin. We read every brief ourselves.',
    overline: 'CONFIDENTIAL BRIEF',
    title: 'To CuratedHomes',
    matters: [
      'A house in formation',
      'A house in renewal',
      'A house seeking a voice',
      'A confidential matter',
    ],
    horizons: ['Within the season', 'Within the year', 'In its own time'],
  },
}

// Section registry — drives the rail, the index menu, and section order.
export const SECTION_ORDER = [
  { key: 'hero', num: '00', name: 'HERO', label: 'Hero' },
  { key: 'philosophy', num: '01', name: 'PHILOSOPHY', label: 'Philosophy' },
  { key: 'method', num: '02', name: 'METHOD', label: 'Method' },
  { key: 'disciplines', num: '03', name: 'DISCIPLINES', label: 'Disciplines' },
  { key: 'engagements', num: '04', name: 'ENGAGEMENTS', label: 'Engagements' },
  { key: 'difference', num: '05', name: 'DIFFERENCE', label: 'Difference' },
  { key: 'enquiry', num: '06', name: 'ENQUIRY', label: 'Enquiry' },
]

// The rail shows 01–06 (the hero has no number).
export const RAIL_SECTIONS = SECTION_ORDER.filter((s) => s.key !== 'hero')

// ---- editable section registry (Phase 9) ----
// The list of sections (order, display label, visibility) lives in Firestore
// at siteSections/{locale}; this is the seed/fallback. Each entry stores only
// { key, label, hidden } — `key` is immutable (it maps to a bespoke section
// component + its content subtree). The two-digit number and the uppercase
// rail name are DERIVED from order, so renaming/reordering needs no extra data.
export const DEFAULT_SECTIONS = SECTION_ORDER.map((s) => ({
  key: s.key,
  label: s.label,
  hidden: false,
}))

// Resolve stored/seed items into the ordered render list the app consumes:
// hero is pinned first and unnumbered (00); the rest are numbered by position.
// Unknown keys (not backed by a component) are dropped; missing known keys are
// appended, so code and DB can never drift out of sync.
export function resolveSections(remoteItems) {
  const known = new Map(DEFAULT_SECTIONS.map((s) => [s.key, s]))
  const incoming = Array.isArray(remoteItems)
    ? remoteItems.filter((it) => it && known.has(it.key))
    : []
  const seen = new Set(incoming.map((it) => it.key))
  for (const s of DEFAULT_SECTIONS) if (!seen.has(s.key)) incoming.push({ ...s })

  const hero = incoming.find((it) => it.key === 'hero') || { key: 'hero', label: 'Hero' }
  const rest = incoming.filter((it) => it.key !== 'hero')
  return [hero, ...rest].map((it, i) => ({
    key: it.key,
    label: it.label ?? known.get(it.key)?.label ?? it.key,
    hidden: it.key === 'hero' ? false : !!it.hidden,
    num: String(i).padStart(2, '0'),
  }))
}

// The default resolved list (with derived nums) for immediate, pre-load render.
export const RESOLVED_DEFAULT_SECTIONS = resolveSections(DEFAULT_SECTIONS)
